import {
  useEffect, useRef, useState, useMemo,
} from 'react';
import {
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import {
  Message, GetParticipantsResponse, MessageType, Conversation, TripMatch, TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import useSWRInfinite from 'swr/infinite';
import { usePusherStore } from '../store';

const useChatData = ({ conversationId }: { conversationId?: string }) => {
  const limit = 30;
  const apiClient = useApiClient();
  const conversation = useFetch<Conversation>(apiClient.conversationsClient.getConversation, [conversationId], !!conversationId);
  const participants = useFetch<GetParticipantsResponse>(apiClient.conversationsClient.getParticipants, [conversationId ?? undefined], conversationId !== undefined);

  const getKey = (pageIndex: number, previousPageData: Message[] | null) => {
    if (previousPageData && !previousPageData.length) return null;
    return [conversationId, { params: { offset: previousPageData ? previousPageData[0]?.id : undefined, limit } }];
  };

  const fetcher = async ([id, params]: [any, any]) => {
    if (typeof id !== 'string') {
      return [];
    }
    return apiClient.conversationsClient.getMessages(id, params as any);
  };

  const paginatedMessages = useSWRInfinite(getKey, fetcher);

  const loadMoreMessages = async () => {
    paginatedMessages.setSize((size) => size + 1);
  };

  const sortedMessages = useMemo(() => {
    if (!paginatedMessages.data) return [];
    return [...paginatedMessages.data].reverse().flat();
  }, [paginatedMessages.data]);

  const hasMoreMessages = useMemo(() => {
    if (!paginatedMessages.data) return true;

    const pages = paginatedMessages.data;
    const lastPage = pages[pages.length - 1] ?? [];

    return lastPage.length > 0;
  }, [paginatedMessages.data]);

  const messages = {
    data: sortedMessages,
    isLoading: paginatedMessages.isLoading,
    isValidating: paginatedMessages.isValidating,
    error: paginatedMessages.error,
    mutate: paginatedMessages.mutate,
  };

  return {
    conversation,
    participants,
    messages,
    loadMoreMessages,
    hasMoreMessages,
  };
};

type UseZicassoChatProps = {
  conversationId?: string,
  viewerId?: string,
  /**
   * Optional trip match / trip request IDs. When provided, the hook keeps their SWR caches
   * in sync after a message is fully processed by the backend (MESSAGE_PROCESSED Pusher event)
   * so message-state side effects on the trip match and trip request are reflected immediately.
   */
  tripMatchId?: string,
  tripRequestId?: string,
  /**
   * When false, skips Pusher subscribe + event bindings. Use for extra mounts that only need
   * sendMessage / mutate (e.g. parent containers that also render the main chat); the main
   * chat mount must keep this true or messages will not refresh in real time.
   */
  subscribeToRealtime?: boolean,
};

const useZicassoChat = ({
  conversationId,
  viewerId,
  tripMatchId,
  tripRequestId,
  subscribeToRealtime = true,
}: UseZicassoChatProps) => {
  const [lastSeenMessageId, setLastSeenMessageId] = useState<string | undefined>(undefined);
  const initializedLastSeenMessageId = useRef<boolean>(false);

  const apiClient = useApiClient();
  const {
    conversation,
    participants,
    messages,
    loadMoreMessages,
    hasMoreMessages,
  } = useChatData({ conversationId });

  // Fall back to IDs available on the conversation when the caller does not have them
  // (e.g. routes without :tripMatchId/:tripRequestId in the URL).
  const effectiveTripMatchId = tripMatchId ?? conversation.data?.tripMatchId;
  const effectiveTripRequestId = tripRequestId ?? conversation.data?.tripRequestId;

  // Shared SWR cache with the consumer container — calling mutate here refreshes
  // the data wherever the same key is used.
  const tripMatch = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [effectiveTripMatchId], !!effectiveTripMatchId);
  const tripRequest = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [effectiveTripRequestId], !!effectiveTripRequestId);

  // Only set lastSeenMessageId once when conversation is loaded
  useEffect(() => {
    if (initializedLastSeenMessageId.current || !conversation?.data) return;

    const viewerMessageState = conversation.data.messageState.find((m) => m.personId === viewerId);
    setLastSeenMessageId(viewerMessageState?.lastMessageRead);
    initializedLastSeenMessageId.current = true;
  }, [conversation]);

  const sendMessage = async (message: Partial<Message>) => {
    if (!conversationId || !message.text) {
      throw new Error('Missing conversationId or message text');
    }

    const messageToSend = {
      conversationId,
      messageType: MessageType.Text,
      ...message,
    };

    try {
      const postMessage = await apiClient.conversationsClient.postMessage(conversationId, messageToSend);
      // Update last seen message id
      setLastSeenMessageId(postMessage.id);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log(error);
      throw error;
    }
  };

  const updateMessages = async () => {
    await messages.mutate();
  };

  const deleteMessage = async (messageId: string) => {
    if (!conversationId) {
      throw new Error('Missing conversationId');
    }

    try {
      await apiClient.conversationsClient.deleteMessage(conversationId, messageId);
      await updateMessages(); // Refetch messages to remove the deleted message
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log(error);
      throw error;
    }
  };

  // PUSHER
  const channelName = conversationId ? `presence-${conversationId}` : '';

  const {
    bindToEvent,
    ready,
    subscribeToChannel,
    subscribedChannels,
    unbindFromEvent,
    unsubscribeFromChannel,
  } = usePusherStore();

  // Subscribe to Pusher Channel (single "owner" per screen should use subscribeToRealtime: true)
  useEffect(() => {
    if (!subscribeToRealtime) return undefined;
    if (ready && channelName) {
      subscribeToChannel(channelName);
    }

    return () => {
      if (subscribeToRealtime && ready) {
        unsubscribeFromChannel(channelName);
      }
    };
  }, [ready, channelName, subscribeToRealtime]);

  // Listen to new messages and update messages and conversation
  useEffect(() => {
    if (!subscribeToRealtime) return undefined;
    if (subscribedChannels[channelName]) {
      const messageRefreshEvents = [MessageType.Text, MessageType.SMSText];
      messageRefreshEvents.forEach((eventName) => {
        bindToEvent(channelName, eventName, () => {
          updateMessages();
        });
      });

      bindToEvent(channelName, 'MESSAGE_READ', () => {
        conversation.mutate(); // Refetch conversation on MESSAGE_READ pusher event
      });

      bindToEvent(channelName, 'PERSON_ADDED', () => {
        participants.mutate();
      });

      // Fired by travel-service after all create-message side effects are applied
      // (trip match + trip request message state updates and notifications).
      // Also refresh messages here because non-read-trackable types do not trigger Message push
      bindToEvent(channelName, 'MESSAGE_PROCESSED', () => {
        updateMessages();
        if (effectiveTripMatchId) tripMatch.mutate();
        if (effectiveTripRequestId) tripRequest.mutate();
      });
    }

    return () => {
      if (subscribedChannels[channelName]) {
        unbindFromEvent(channelName, MessageType.Text);
        unbindFromEvent(channelName, MessageType.SMSText);
        unbindFromEvent(channelName, 'MESSAGE_READ');
        unbindFromEvent(channelName, 'PERSON_ADDED');
        unbindFromEvent(channelName, 'MESSAGE_PROCESSED');
      }
    };
  }, [subscribeToRealtime, subscribedChannels, channelName, effectiveTripMatchId, effectiveTripRequestId]);

  return {
    conversation: conversation.data,
    isError: !!participants.error || !!messages.error,
    isLoading: participants.isLoading || messages.isLoading,
    isValidating: messages.isValidating,
    messages: messages.data,
    participants: participants.data,
    lastSeenMessageId,
    updateMessages,
    sendMessage,
    deleteMessage,
    loadMoreMessages,
    hasMoreMessages,
  };
};

export default useZicassoChat;
